import React, { useState } from 'react';
import { Container, Typography, Box, Alert, Paper, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OrganizationForm from '../../components/OrganizationForm';
import { OrganizationFormData } from '../../types/Organization';
import { useUser } from '../../contexts/UserContext';
import { createOrganization } from '../../services/organizationService';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const CreateOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newOrgId, setNewOrgId] = useState<string | null>(null);

  const handleSubmit = async (formData: OrganizationFormData) => {
    if (!currentUser?.uid) {
      setError('You must be logged in to create an organization');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the service to create the organization
      const orgId = await createOrganization(currentUser.uid, formData);
      setNewOrgId(orgId);
      setSuccess(true);
      
      // Redirect after a short delay to let the user see the success message
      setTimeout(() => {
        navigate(`/organizations/${orgId}`);
      }, 1500);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Create a New Organization
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success ? (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Organization created successfully! Redirecting you to the organization page...
          </Alert>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/organizations/${newOrgId}`)}
            >
              Go to Organization Page
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Create an organization to manage events, invite members, and build your community. Organizations can host public events, sell tickets, and more.
          </Typography>

          <OrganizationForm
            onSubmit={handleSubmit}
            loading={loading}
            formTitle="Create Organization"
            submitButtonText="Create Organization"
          />
        </>
      )}
    </Container>
  );
};

export default CreateOrganizationPage; 